import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { Star, Flag, Play, MapPin } from "lucide-react";
import { useAuth } from "../lib/auth";
import { htmlToPlainText } from "../lib/text";

/**
 * Home — layout allineato al mockup PDF (schermata "Catalogo cacce"):
 *  - Header con logo Odismap + pill "Accedi" verde (in Layout, non qui)
 *  - Hero minimale: eyebrow arancione "GIOCARE A TESTA ALTA" + titolo Poppins
 *  - Card percorso immersive: foto full-bleed con badge età/difficoltà + banda
 *    verde forest in basso con titolo, descrizione, prezzo e CTA arancione "GIOCA ▶"
 */

// Deriva la fascia d'età da spec_min_age. Se non specificato mostra "PER TUTTI".
const ageLabel = (route) => {
  const min = route?.spec_min_age;
  if (min == null || min === 0) return "PER TUTTI";
  if (min <= 4) return `${min}-6 ANNI`;
  if (min <= 7) return `${min}-${min + 2} ANNI`;
  if (min <= 11) return `${min}-${min + 3} ANNI`;
  return `${min}+ ANNI`;
};

// Deriva la difficoltà dalla spec_sfida_mentale (0-5).
const difficultyLabel = (route) => {
  const s = Number(route?.spec_sfida_mentale ?? 0);
  if (s <= 1) return "FACILE";
  if (s <= 3) return "MEDIA";
  return "DIFFICILE";
};

const priceLabel = (route) => {
  const p = Number(route?.price_eur ?? 0);
  if (p <= 0) return "GRATIS";
  return `€ ${p.toFixed(2).replace(".", ",")}`;
};

function RouteCard({ route, onPlay }) {
  const points = route.max_total_points ?? 0;
  const tappe = route.points_count ?? 0;
  return (
    <article
      className="rounded-3xl overflow-hidden shadow-tactile bg-forest text-white"
      data-testid={`route-card-${route.route_id}`}
    >
      {/* Foto full-bleed con overlay badge */}
      <div className="relative aspect-[4/5] sm:aspect-[16/12] bg-forest-hover">
        {route.image_url ? (
          <img
            src={route.image_url}
            alt={route.title}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
            Nessuna immagine
          </div>
        )}

        {/* Top-left: mini-logo Odismap in card bianca */}
        <div className="absolute top-3 left-3 w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center">
          <img src="/odismap-logo.png" alt="Odismap" className="h-10 w-auto" draggable={false} />
        </div>

        {/* Top-right: badge età + difficoltà */}
        <div className="absolute top-3 right-3 flex gap-2">
          <span className="px-3 py-1.5 rounded-full bg-gold text-white text-xs font-extrabold tracking-wide shadow" data-testid={`route-age-${route.route_id}`}>
            {ageLabel(route)}
          </span>
          <span className="px-3 py-1.5 rounded-full bg-white/85 text-forest-hover text-xs font-extrabold tracking-wide shadow" data-testid={`route-difficulty-${route.route_id}`}>
            {difficultyLabel(route)}
          </span>
        </div>

        {/* Bottom-left: punti + tappe badges */}
        <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end gap-2">
          <div className="flex gap-2 flex-wrap">
            {points > 0 && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-hover/95 text-white text-xs font-extrabold shadow" data-testid={`route-points-${route.route_id}`}>
                <Star size={13} className="text-gold fill-gold" /> fino a {points} punti
              </span>
            )}
            {tappe > 0 && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-hover/95 text-white text-xs font-extrabold shadow" data-testid={`route-tappe-${route.route_id}`}>
                <Flag size={13} className="text-compass" /> {tappe} {tappe === 1 ? "tappa" : "tappe"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Banda inferiore verde con titolo, descrizione, prezzo e CTA */}
      <div className="p-5 sm:p-6 text-center flex flex-col items-center gap-3">
        {route.city && (
          <div
            className="inline-flex items-center gap-1.5 text-white/80 text-xs sm:text-sm font-bold uppercase tracking-widest"
            data-testid={`route-city-${route.route_id}`}
          >
            <MapPin size={14} className="text-gold" />
            <span>{route.city}</span>
          </div>
        )}
        <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight" data-testid={`route-title-${route.route_id}`}>
          {route.title}
        </h3>
        {route.description && (
          <p className="text-white/85 text-sm sm:text-base leading-snug max-w-md line-clamp-3" data-testid={`route-description-${route.route_id}`}>
            {htmlToPlainText(route.description)}
          </p>
        )}
        <div className="w-full flex items-center justify-between mt-2">
          <div className="text-2xl sm:text-3xl font-display font-extrabold text-white" data-testid={`route-price-${route.route_id}`}>
            {priceLabel(route)}
          </div>
          <button
            onClick={onPlay}
            className="inline-flex items-center gap-2 pl-6 pr-5 py-3 rounded-full bg-gold hover:bg-gold-hover text-white font-display font-extrabold text-lg shadow-tactile transition-all active:translate-y-0.5"
            data-testid={`route-play-btn-${route.route_id}`}
          >
            GIOCA <Play size={18} className="fill-white" />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState(null);

  useEffect(() => {
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => setRoutes([]));
  }, []);

  return (
    <div className="space-y-6">
      {/* Hero minimale */}
      <section className="text-center pt-2" data-testid="home-hero">
        <div className="text-gold font-display font-extrabold text-xs sm:text-sm tracking-[0.22em] uppercase mb-3">
          Giocare a testa alta
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-forest-hover leading-tight">
          {user ? "Scegli la tua avventura" : "Scegli la tua avventura"}
        </h1>
        <p className="text-ink-500 text-sm sm:text-base mt-2 max-w-md mx-auto">
          Scegli il parco · ogni età ha la sua caccia al tesoro.
        </p>
      </section>

      {/* Lista card immersive — 1 colonna su mobile, 2 su tablet+ */}
      <section className="space-y-5 sm:space-y-6 max-w-2xl mx-auto">
        {routes === null && (
          <>
            <Skeleton className="h-[520px] rounded-3xl" />
            <Skeleton className="h-[520px] rounded-3xl" />
          </>
        )}

        {routes?.length === 0 && (
          <div className="p-8 rounded-3xl bg-white border border-outline text-center text-ink-500">
            Nessuna caccia al tesoro disponibile al momento.
          </div>
        )}

        {routes?.map((r) => (
          <RouteCard key={r.route_id} route={r} onPlay={() => navigate(`/routes/${r.route_id}`)} />
        ))}
      </section>
    </div>
  );
}
